import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, Locale } from "date-fns";
import Badge from "src/common/components/Badge/Badge";
import Spinner from "src/common/components/Loader/Spinner";
import { ReleaseFeedEntry } from "src/model/model";
import ReleaseChanges from "src/features/releases/components/ReleaseChanges";
import { fetchReleaseFeed } from "src/features/releases/api/releasesApi";

/**
 * One self-contained, independently-scrollable release feed for a single surface
 * ("vr" = Spatial Studio, "web" = Editor Web). Owns its own pagination, month
 * grouping, sticky month headers, deep-link scroll and highlight.
 *
 * The `/releases` page renders a single full-width column for the "vr" / "web"
 * tabs, and two side-by-side columns for the "all" tab — each column scrolls on
 * its own while the page around them stays put (`title` is shown only in that
 * split view so each side is labelled).
 *
 * A "both"-surface entry is returned by both the vr and web fetches, so it can
 * appear in both columns at once; DOM ids are therefore namespaced by surface
 * (`release-${surface}-${id}`) to stay unique.
 */

const PAGE_SIZE = 20;

// Chip colors mirror the popover (features/releases/components/ReleaseBell.tsx)
// so the two surfaces read identically.
const surfaceChip: Record<ReleaseFeedEntry["surface"], { color: string; textColor: string }> = {
  web: { color: "bg-blue-100", textColor: "text-blue-700" },
  vr: { color: "bg-purple-100", textColor: "text-purple-700" },
  both: { color: "bg-green-100", textColor: "text-green-700" },
};

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

interface ReleaseColumnProps {
  surface: "web" | "vr";
  locale: Locale;
  /** Column heading, shown only when several columns sit side by side. */
  title?: string;
}

const ReleaseColumn = ({ surface, locale, title }: ReleaseColumnProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [entries, setEntries] = useState<ReleaseFeedEntry[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  // Last hash successfully scrolled to. Comparing against the current hash (not a
  // boolean) fires each distinct deep-link once while loadMore re-renders (same
  // hash, entries change) don't trigger a second scroll.
  const didScrollRef = useRef("");
  // Cancel handle for any in-flight loadMore, replaced on each call and invoked
  // by the initial-load cleanup when the surface changes.
  const loadMoreCancelRef = useRef<() => void>(() => undefined);

  // (Re)load the first page whenever the surface changes.
  useEffect(() => {
    let cancelled = false;
    loadMoreCancelRef.current();
    setInitialLoading(true);
    setEntries([]);
    setOffset(0);
    fetchReleaseFeed({ limit: PAGE_SIZE, offset: 0, surface })
      .then((batch) => {
        if (cancelled) return;
        setEntries(batch);
        setHasMore(batch.length === PAGE_SIZE);
      })
      .catch(() => {
        if (cancelled) return;
        setEntries([]);
        setHasMore(false);
      })
      .finally(() => {
        if (!cancelled) setInitialLoading(false);
      });
    return () => {
      cancelled = true;
      loadMoreCancelRef.current();
    };
  }, [surface]);

  const loadMore = useCallback(() => {
    let cancelled = false;
    loadMoreCancelRef.current = () => {
      cancelled = true;
    };
    const nextOffset = offset + PAGE_SIZE;
    setLoadingMore(true);
    fetchReleaseFeed({ limit: PAGE_SIZE, offset: nextOffset, surface })
      .then((batch) => {
        if (cancelled) return;
        setEntries((prev) => [...prev, ...batch]);
        setOffset(nextOffset);
        setHasMore(batch.length === PAGE_SIZE);
      })
      .catch(() => {
        if (cancelled) return;
        setHasMore(false);
      })
      .finally(() => {
        if (!cancelled) setLoadingMore(false);
      });
  }, [offset, surface]);

  // Deep-link: once the target card is in the DOM (and belongs to this column),
  // scroll to it and flash a highlight. Each distinct hash gets one scroll; the
  // owning column(s) act, others bail early.
  useEffect(() => {
    if (didScrollRef.current === location.hash) return undefined;
    const match = location.hash.match(/^#release-(\d+)$/);
    if (!match) return undefined;
    const id = Number(match[1]);
    if (!entries.some((e) => e.id === id)) return undefined;
    const el = document.getElementById(`release-${surface}-${id}`);
    if (!el) return undefined;
    didScrollRef.current = location.hash;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(id);
    const timeout = setTimeout(() => setHighlightId(null), 2000);
    return () => clearTimeout(timeout);
  }, [location.hash, entries, surface]);

  // Group the (already DESC-sorted) entries into consecutive month buckets.
  const groups = useMemo(() => {
    const out: { label: string; items: ReleaseFeedEntry[] }[] = [];
    entries.forEach((entry) => {
      const label = entry.published_at_user_feed
        ? capitalize(format(new Date(entry.published_at_user_feed), "MMMM yyyy", { locale }))
        : "";
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(entry);
      else out.push({ label, items: [entry] });
    });
    return out;
  }, [entries, locale]);

  // The newest entry of each surface value present is that surface's "current
  // version". Entries arrive DESC-sorted from offset 0, so the first occurrence
  // of a surface value is its latest; recomputed as more pages load.
  const currentIds = useMemo(() => {
    const seen = new Set<ReleaseFeedEntry["surface"]>();
    const ids = new Set<number>();
    entries.forEach((entry) => {
      if (!seen.has(entry.surface)) {
        seen.add(entry.surface);
        ids.add(entry.id);
      }
    });
    return ids;
  }, [entries]);

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {title && (
        <h2 className="shrink-0 border-b border-gray-200 px-2 pb-3 text-lg font-bold text-black">
          {title}
        </h2>
      )}

      {/* No top padding here: the sticky month header must pin flush to the
          scrollport top, else cards peek through the gap above it. */}
      <div className="personalize-scroll-visible min-h-0 flex-1 overflow-y-auto px-2">
        {initialLoading ? (
          <div className="relative h-40">
            <Spinner />
          </div>
        ) : entries.length === 0 ? (
          <p className="py-16 text-center text-gray-500">{t("pages.releases.empty")}</p>
        ) : (
          <div className="pb-12">
            {groups.map((group) => (
              <section key={group.label || "undated"} className="mb-10">
                {/* Solid, full-width month bar: sticks flush to the scrollport top
                    (no padding above it) with a high z so cards scroll behind it. */}
                <h3 className="sticky top-0 z-20 -mx-2 bg-slate-100 px-2 py-3 text-lg font-semibold text-gray-500">
                  {group.label}
                </h3>
                <div className="space-y-6 border-l-2 border-gray-200 pl-6">
                  {group.items.map((entry) => (
                    <article
                      key={entry.id}
                      id={`release-${surface}-${entry.id}`}
                      className={`relative scroll-mt-24 rounded-lg bg-white p-6 shadow transition-shadow ${
                        highlightId === entry.id ? "ring-2 ring-inset ring-green-500" : ""
                      }`}
                    >
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <Badge
                          color={surfaceChip[entry.surface].color}
                          textColor={surfaceChip[entry.surface].textColor}
                          text={t(`pages.releases.surface.${entry.surface}`)}
                        />
                        {currentIds.has(entry.id) && (
                          <span
                            title={t("pages.releases.current")}
                            aria-label={t("pages.releases.current")}
                            className="inline-block h-2 w-2 shrink-0 rounded-full bg-green-500"
                          />
                        )}
                        {entry.is_mandatory_update && (
                          <Badge
                            color="bg-green-100"
                            textColor="text-green-700"
                            text={t("pages.releases.mandatory")}
                            title={t("pages.releases.mandatoryTooltip")}
                          />
                        )}
                        {entry.version && (
                          <span className="text-sm text-gray-400">
                            {entry.version}
                            {entry.linked_unity_release?.version
                              ? ` (Unity ${entry.linked_unity_release.version})`
                              : ""}
                          </span>
                        )}
                        {entry.published_at_user_feed && (
                          <span className="ml-auto text-sm text-gray-400">
                            {format(new Date(entry.published_at_user_feed), "d MMMM yyyy", {
                              locale,
                            })}
                          </span>
                        )}
                      </div>

                      <h4 className="mb-3 break-words text-xl font-bold text-black">
                        {entry.headline}
                      </h4>

                      <ReleaseChanges html={entry.changes_html} />

                      {entry.optional_release_notice && (
                        <p className="mt-3 break-words text-sm text-gray-600">
                          {entry.optional_release_notice}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            ))}

            {hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="btn-primary-outline"
                >
                  {loadingMore ? t("pages.releases.loading") : t("pages.releases.loadMore")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReleaseColumn;
